import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CreditCard, Inbox, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { live, LIVE_SLOW_MS } from "@/lib/live-query";
import { useNewRequestsSwitch } from "@/lib/new-requests";
import type { RequestsView } from "@/lib/requests";

/**
 * Top-of-Grid attention signposts, so dispatchers notice work without leaving
 * the board:
 *   - new online bookings waiting (sky) → the Online Booking intake, and
 *   - jobs blocked from dispatch by a missing card on file (red) → the
 *     pre-dispatch payment burn-down.
 *
 * Each banner renders only when its count > 0. Best-effort: a failed/forbidden
 * count simply hides its banner (retry: false) rather than breaking the board —
 * the card count endpoint needs jobs.manage, so a scheduler without it just
 * won't see that banner.
 *
 * Readability (#804): these banners used to be a 10%-alpha tint with no
 * backdrop blur. The Grid paints on top of `BlueprintBackdrop`, whose logo
 * watermark renders at full strength, so at that alpha the watermark read
 * straight through the banner and the copy lost its contrast floor — bookings
 * were arriving and nobody could see the sign. They now use the same house
 * pattern as every other board surface (`TriageAlertBar`, `GridLegend`,
 * `DaySwimlane`, the backlog cards): an opaque tint plus `backdrop-blur-sm`,
 * so nothing bleeds through. The count also leads as a solid filled chip
 * rather than plain inline text, so it carries more weight than the sentence
 * under it and holds its own against the amber triage bar directly beneath.
 */
export function GridAttentionBar() {
    // New requests (10/28): with "Try New requests" on, the signpost counts
    // the ONE list (New + Trying to reach, the chip's number) and opens it.
    const newRequests = useNewRequestsSwitch();
    const bookingsQ = useQuery({
        queryKey: ["grid", "pending-bookings-count"],
        ...live(LIVE_SLOW_MS),
        queryFn: () => api.get<{ count: number }>("/api/online-bookings/pending-count"),
        staleTime: 60_000,
        retry: false,
        enabled: newRequests.loaded && !newRequests.on,
    });
    const requestsQ = useQuery({
        queryKey: ["requests"],
        ...live(LIVE_SLOW_MS),
        queryFn: () => api.get<RequestsView>("/api/requests"),
        staleTime: 30_000,
        retry: false,
        enabled: newRequests.on,
    });
    const cardBlockedQ = useQuery({
        queryKey: ["grid", "card-blocked-count"],
        ...live(LIVE_SLOW_MS),
        queryFn: () => api.get<{ count: number }>("/api/jobs/pre-dispatch-payment"),
        staleTime: 60_000,
        retry: false,
    });

    const newBookings = newRequests.on ? (requestsQ.data?.pill.count ?? 0) : (bookingsQ.data?.count ?? 0);
    const cardBlocked = cardBlockedQ.data?.count ?? 0;
    if (newBookings === 0 && cardBlocked === 0) return null;

    return (
        <div className="space-y-2">
            {newBookings > 0 && (
                <Link
                    to={newRequests.on ? "/requests" : "/booking"}
                    data-testid="grid-attention-bookings"
                    className="flex items-center gap-3 rounded-lg border border-sky-400 bg-sky-50/95 px-3.5 py-2.5 shadow-sm backdrop-blur-sm transition-colors hover:bg-sky-100/95 dark:border-sky-700 dark:bg-sky-950/90 dark:hover:bg-sky-900/90"
                >
                    <span className="flex shrink-0 items-center gap-1.5 rounded-md bg-sky-700 px-2 py-1 text-white dark:bg-sky-500">
                        <Inbox className="h-4 w-4" />
                        <span className="text-sm font-bold tabular-nums">{newBookings}</span>
                    </span>
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-sky-950 dark:text-sky-50">
                            {newRequests.on
                                ? `New request${newBookings === 1 ? "" : "s"} to call back`
                                : `New online booking${newBookings === 1 ? "" : "s"} waiting`}
                        </div>
                        <div className="text-xs text-sky-900/80 dark:text-sky-100/75">
                            {newRequests.on
                                ? "Customers who asked for a test. Call them from the New requests list."
                                : "Fresh from the website — review and turn them into jobs so they can be scheduled."}
                        </div>
                    </div>
                    <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-sky-800 dark:text-sky-200">
                        Review <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                </Link>
            )}
            {cardBlocked > 0 && (
                <Link
                    to="/jobs/pre-dispatch-payment"
                    data-testid="grid-attention-card-blocked"
                    className="flex items-center gap-3 rounded-lg border border-red-400 bg-red-50/95 px-3.5 py-2.5 shadow-sm backdrop-blur-sm transition-colors hover:bg-red-100/95 dark:border-red-800 dark:bg-red-950/90 dark:hover:bg-red-900/90"
                >
                    <span className="flex shrink-0 items-center gap-1.5 rounded-md bg-red-600 px-2 py-1 text-white dark:bg-red-600">
                        <CreditCard className="h-4 w-4" />
                        <span className="text-sm font-bold tabular-nums">{cardBlocked}</span>
                    </span>
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-red-950 dark:text-red-50">
                            Job{cardBlocked === 1 ? "" : "s"} can’t be dispatched — no card on file
                        </div>
                        <div className="text-xs text-red-900/80 dark:text-red-100/75">
                            These accounts require a card before a truck goes out. Send a card link or collect one to
                            clear them.
                        </div>
                    </div>
                    <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-red-800 dark:text-red-200">
                        Review &amp; send links <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                </Link>
            )}
        </div>
    );
}
